// Тур по продукту поверх макета приложения.
//
// Не подсветка настоящих кнопок, а отдельная сцена: настоящий экран у нового
// человека пуст — подсвечивать в нём нечего, а рассказ тура именно про то,
// что получается, когда поездка уже собрана.
//
// Порядок шагов задаёт STEPS из scenario.ts, там же он и объяснён. Шаг —
// единственное состояние: сцена анимируется от него сама (см. Stage), поэтому
// здесь нет ни таймеров, ни автопрокрутки. Человек листает сам и может
// вернуться на любой шаг точкой внизу.
import { useCallback, useState, type KeyboardEvent, type ReactElement } from 'react';
import { useT } from '../i18n';
import { Modal } from '../ui/shell';
import { Stage } from './Stage';
import { STEPS } from './scenario';
import styles from './OnboardingTour.module.css';

const NUMBERS = STEPS.map((_, i) => i + 1);

export interface OnboardingTourProps {
  onClose(): void;
}

export function OnboardingTour({ onClose }: OnboardingTourProps): ReactElement {
  const t = useT();
  const [step, setStep] = useState(1);

  const last = step === STEPS.length;

  const go = useCallback((next: number) => {
    setStep(Math.min(STEPS.length, Math.max(1, next)));
  }, []);

  // Стрелками листается вся панель: тур смотрят, а не заполняют, и тянуться
  // мышью к кнопке ради следующего шага незачем. Esc закрывает — это уже Modal.
  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'ArrowRight') go(step + 1);
    else if (e.key === 'ArrowLeft') go(step - 1);
    else return;
    e.preventDefault();
  }

  const footer = (
    <div className={styles.footer}>
      <button type="button" className={styles.skip} onClick={onClose}>
        {t('onboarding.skip')}
      </button>

      <div
        className={styles.dots}
        role="group"
        aria-label={t('onboarding.step', { current: step, total: STEPS.length })}
      >
        {NUMBERS.map((n) => (
          <button
            key={n}
            type="button"
            className={`${styles.dot} ${n === step ? styles.dotActive : ''}`}
            aria-label={t('onboarding.goto', { n })}
            aria-current={n === step ? 'step' : undefined}
            onClick={() => go(n)}
          />
        ))}
      </div>

      <div className={styles.nav}>
        {/* Кнопка «назад» не исчезает на первом шаге, а гаснет: иначе ряд
            подпрыгивал бы на каждом переходе. */}
        <button
          type="button"
          className="btn btn-sm"
          disabled={step === 1}
          onClick={() => go(step - 1)}
        >
          {t('onboarding.back')}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => (last ? onClose() : go(step + 1))}
        >
          {t(last ? 'onboarding.done' : 'onboarding.next')}
        </button>
      </div>
    </div>
  );

  return (
    <Modal title={t('onboarding.title')} onClose={onClose} footer={footer} wide>
      <div className={styles.body} onKeyDown={handleKeyDown}>
        <Stage step={step} />

        <div className={styles.text}>
          <h3 className={styles.stepTitle}>{t(`onboarding.s${step}.title`)}</h3>
          <p className={styles.stepText}>{t(`onboarding.s${step}.text`)}</p>
        </div>

        {/* Макет для скринридера скрыт, поэтому номер шага объявляется здесь. */}
        <p className="visually-hidden" aria-live="polite">
          {t('onboarding.step', { current: step, total: STEPS.length })}
        </p>
      </div>
    </Modal>
  );
}
